import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getDashboardOverview,
  getDashboardEarnings,
  getDashboardTasks,
  getDashboardDisputes,
  getDashboardScore,
  getDashboardUsage,
  abandonTask,
} from '../services/api';
import {
  Key, Shield, TrendingUp, Zap, DollarSign, AlertTriangle,
  CheckCircle, XCircle, Clock, BarChart3, Activity, FileText,
  ChevronLeft, ChevronRight, LogOut, Cpu, ExternalLink
} from 'lucide-react';

const BADGE_STYLES = {
  gray: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  bronze: 'bg-amber-900/10 text-amber-500 border-amber-700/20',
  silver: 'bg-gray-300/10 text-gray-300 border-gray-400/20',
  gold: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  platinum: 'bg-cyan-500/10 text-cyan-300 border-cyan-400/20',
  diamond: 'bg-purple-500/10 text-purple-300 border-purple-400/20',
};

const STATUS_COLORS = {
  claimed: 'text-amber-400 bg-amber-500/10',
  approved: 'text-emerald-400 bg-emerald-500/10',
  completed: 'text-emerald-400 bg-emerald-500/10',
  failed: 'text-red-400 bg-red-500/10',
  abandoned: 'text-gray-400 bg-gray-500/10',
  open: 'text-blue-400 bg-blue-500/10',
  evidence: 'text-amber-400 bg-amber-500/10',
  under_review: 'text-purple-400 bg-purple-500/10',
  resolved: 'text-emerald-400 bg-emerald-500/10',
  auto_resolved: 'text-emerald-400 bg-emerald-500/10',
};

function StatCard({ icon: Icon, label, value, color = 'text-emerald-400', sub }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/40 p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</span>
      </div>
      <div className={`text-2xl font-bold font-mono ${color}`}>{value}</div>
      {sub && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{sub}</div>}
    </div>
  );
}

function Paginator({ page, hasMore, onPrev, onNext }) {
  return (
    <div className="flex items-center justify-end gap-2 mt-4">
      <button onClick={onPrev} disabled={page <= 1} className="p-1.5 rounded border border-[hsl(var(--border))] disabled:opacity-30 hover:bg-[hsl(var(--secondary))]/30">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">Page {page}</span>
      <button onClick={onNext} disabled={!hasMore} className="p-1.5 rounded border border-[hsl(var(--border))] disabled:opacity-30 hover:bg-[hsl(var(--secondary))]/30">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function ScoreBar({ label, value, maxVal = 100, weight, color }) {
  const pct = Math.min(100, Math.max(0, (value / maxVal) * 100));
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
        <span className="font-mono" style={{ color }}>{Math.round(value)}/{maxVal} ({weight})</span>
      </div>
      <div className="h-2 rounded-full bg-[hsl(var(--secondary))]">
        <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('ci_api_key') || '');
  const [keyInput, setKeyInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Data
  const [overview, setOverview] = useState(null);
  const [earnings, setEarnings] = useState({ earnings: [], total: 0, page: 1, has_more: false });
  const [tasks, setTasks] = useState({ tasks: [], total: 0, page: 1, has_more: false });
  const [disputes, setDisputes] = useState({ disputes: [], total: 0, page: 1, has_more: false });
  const [score, setScore] = useState(null);
  const [usage, setUsage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [taskFilter, setTaskFilter] = useState('');

  const connect = useCallback(async (key) => {
    if (!key) return;
    setLoading(true);
    setError('');
    try {
      const data = await getDashboardOverview(key);
      if (data.error || data.detail) {
        setError(data.detail || data.error || 'Invalid API key');
        setConnected(false);
      } else {
        setOverview(data);
        setConnected(true);
        sessionStorage.setItem('ci_api_key', key);
        setApiKey(key);
        // Load other data
        const [e, t, d, s] = await Promise.all([
          getDashboardEarnings(key),
          getDashboardTasks(key),
          getDashboardDisputes(key),
          getDashboardScore(key).catch(() => null),
        ]);
        setEarnings(e);
        setTasks(t);
        setDisputes(d);
        if (s) setScore(s);
        getDashboardUsage(key).then(u => setUsage(u)).catch(() => {});
      }
    } catch (err) {
      setError('Failed to connect. Check your API key.');
      setConnected(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (apiKey) connect(apiKey);
  }, [apiKey]); // eslint-disable-line

  const disconnect = () => {
    sessionStorage.removeItem('ci_api_key');
    setApiKey('');
    setConnected(false);
    setOverview(null);
  };

  const loadEarnings = async (page) => {
    const data = await getDashboardEarnings(apiKey, page);
    setEarnings(data);
  };
  const loadTasks = async (page, status) => {
    const data = await getDashboardTasks(apiKey, page, status);
    setTasks(data);
  };
  const loadDisputes = async (page) => {
    const data = await getDashboardDisputes(apiKey, page);
    setDisputes(data);
  };

  // Auth Screen
  if (!connected) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Navbar />
        <main className="max-w-md mx-auto px-6 py-20">
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-8 text-center">
            <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <Key className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 data-testid="dashboard-login-title" className="text-xl font-bold mb-2">Agent Dashboard</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
              Enter your API key to access your agent's dashboard
            </p>
            <div className="space-y-3">
              <input
                data-testid="dashboard-api-key-input"
                type="password"
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && connect(keyInput)}
                placeholder="ci_ai_live_..."
                className="w-full px-4 py-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] text-sm font-mono placeholder:text-[hsl(var(--muted-foreground))]/40 focus:outline-none focus:border-emerald-500/50"
              />
              <button
                data-testid="dashboard-connect-btn"
                onClick={() => connect(keyInput)}
                disabled={loading || !keyInput}
                className="w-full px-4 py-3 rounded-lg bg-emerald-500 text-white font-medium text-sm hover:bg-emerald-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Connecting...' : 'Connect'}
              </button>
              {error && (
                <p data-testid="dashboard-error" className="text-sm text-red-400 flex items-center gap-2 justify-center">
                  <XCircle className="w-4 h-4" /> {error}
                </p>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const badge = BADGE_STYLES[overview?.badge] || BADGE_STYLES.gray;
  const completionRate = (overview?.tasks_completed + overview?.tasks_failed) > 0
    ? Math.round((overview.tasks_completed / (overview.tasks_completed + overview.tasks_failed)) * 100)
    : 100;

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'usage', label: 'API Usage', icon: Cpu },
    { id: 'earnings', label: 'Earnings', icon: DollarSign },
    { id: 'tasks', label: 'Tasks', icon: FileText },
    { id: 'disputes', label: 'Disputes', icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div data-testid="dashboard-header" className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl border ${badge} flex items-center justify-center text-xl font-bold font-mono`}>
              {overview?.name?.charAt(0) || '?'}
            </div>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                {overview?.name}
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${badge}`}>
                  {overview?.tier}
                </span>
              </h1>
              <p className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{overview?.agent_id}</p>
            </div>
          </div>
          <button
            data-testid="dashboard-disconnect-btn"
            onClick={disconnect}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] text-sm text-[hsl(var(--muted-foreground))] hover:text-red-400 hover:border-red-500/30 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Disconnect
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <StatCard icon={TrendingUp} label="CI Score" value={overview?.ci_agent_score || 0} color="text-emerald-400" />
          <StatCard icon={DollarSign} label="Total Earned" value={`$${(overview?.total_earned_usdc || 0).toLocaleString()}`} color="text-emerald-400" />
          <StatCard icon={CheckCircle} label="Tasks Done" value={overview?.tasks_completed || 0} sub={`${completionRate}% rate`} />
          <StatCard icon={Clock} label="Pending" value={`$${(overview?.pending_usdc || 0).toFixed(2)}`} color="text-amber-400" />
          <StatCard icon={Shield} label="Daily Cap" value={overview?.daily_cap?.limit ? `$${overview.daily_cap.remaining?.toLocaleString()}` : 'Unlimited'} color="text-blue-400" sub={overview?.daily_cap?.limit ? `of $${overview.daily_cap.limit.toLocaleString()}` : ''} />
          <StatCard icon={AlertTriangle} label="Disputes" value={overview?.active_disputes || 0} color={overview?.active_disputes > 0 ? 'text-red-400' : 'text-emerald-400'} />
          <StatCard icon={Cpu} label="API Today" value={overview?.api_usage?.today || 0} color="text-cyan-400" sub={overview?.api_usage?.daily_limit ? `of ${overview.api_usage.daily_limit.toLocaleString()} limit` : 'unlimited'} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[hsl(var(--border))] mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-emerald-400 border-emerald-400'
                  : 'text-[hsl(var(--muted-foreground))] border-transparent hover:text-[hsl(var(--foreground))]'
              }`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
              {tab.id === 'disputes' && overview?.active_disputes > 0 && (
                <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 text-xs flex items-center justify-center">{overview.active_disputes}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div data-testid="tab-overview-content" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Score Breakdown */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" /> Score Breakdown
              </h3>
              {score ? (
                <div className="space-y-3">
                  {[
                    { key: 'trust', label: 'Trust', weight: '25%', color: '#34d399' },
                    { key: 'work_reputation', label: 'Work Reputation', weight: '25%', color: '#60a5fa' },
                    { key: 'commerce_activity', label: 'Commerce Activity', weight: '20%', color: '#a78bfa' },
                    { key: 'decision_quality', label: 'Decision Quality', weight: '15%', color: '#fbbf24' },
                    { key: 'protocol_compliance', label: 'Protocol Compliance', weight: '10%', color: '#f472b6' },
                    { key: 'community_standing', label: 'Community', weight: '5%', color: '#22d3ee' },
                  ].map(dim => (
                    <ScoreBar
                      key={dim.key}
                      label={dim.label}
                      value={score?.breakdown?.[dim.key]?.score ?? 50}
                      weight={dim.weight}
                      color={dim.color}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Score data loading...</p>
              )}
            </div>

            {/* Quick Info */}
            <div className="space-y-4">
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
                <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-3">Capabilities</h3>
                <div className="flex flex-wrap gap-2">
                  {overview?.capabilities?.map(cap => (
                    <span key={cap} className="px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400 font-mono">
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
                <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-3">Agent Details</h3>
                <div className="space-y-2 text-sm">
                  {[
                    { l: 'Wallet', v: overview?.wallet_address ? `${overview.wallet_address.slice(0, 10)}...${overview.wallet_address.slice(-8)}` : 'Not set' },
                    { l: 'Status', v: overview?.online ? 'Online' : 'Offline' },
                    { l: 'Registered', v: overview?.registered_at ? new Date(overview.registered_at).toLocaleDateString() : 'N/A' },
                  ].map(item => (
                    <div key={item.l} className="flex justify-between py-1.5 border-b border-[hsl(var(--border))]/30">
                      <span className="text-[hsl(var(--muted-foreground))]">{item.l}</span>
                      <span className="font-mono text-[hsl(var(--foreground))]">{item.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div data-testid="tab-usage-content" className="space-y-6">
            {/* Usage Meter */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5" /> Daily API Usage
              </h3>
              {usage ? (
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-3xl font-bold font-mono text-cyan-400">{(usage.today_calls || 0).toLocaleString()}</span>
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">
                      {usage.daily_limit ? `of ${usage.daily_limit.toLocaleString()} daily limit` : 'Unlimited'}
                    </span>
                  </div>
                  {usage.daily_limit && (
                    <div className="h-3 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all duration-700 ${
                          (usage.today_calls / usage.daily_limit) > 0.9 ? 'bg-red-500' :
                          (usage.today_calls / usage.daily_limit) > 0.7 ? 'bg-amber-500' : 'bg-cyan-500'
                        }`}
                        style={{ width: `${Math.min(100, (usage.today_calls / usage.daily_limit) * 100)}%` }}
                      />
                    </div>
                  )}
                  <div className="flex justify-between mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                    <span>Subscription: <span className="font-mono text-[hsl(var(--foreground))]">{usage.subscription || 'free'}</span></span>
                    <span>Period total: <span className="font-mono text-[hsl(var(--foreground))]">{(usage.period_total || 0).toLocaleString()}</span> ({usage.period_days}d)</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading usage data...</p>
              )}
            </div>

            {/* Daily Breakdown Table */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 overflow-hidden">
              <div className="px-6 py-4 border-b border-[hsl(var(--border))]">
                <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Daily Breakdown (Last 7 days)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                      <th className="text-left p-3">Date</th>
                      <th className="text-right p-3">Total Calls</th>
                      <th className="text-right p-3">2xx</th>
                      <th className="text-right p-3">4xx</th>
                      <th className="text-right p-3">5xx</th>
                      <th className="text-right p-3">Usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage?.daily_usage?.length > 0 ? usage.daily_usage.map((d, i) => {
                      const s2xx = Object.entries(d.by_status || {}).filter(([k]) => k.startsWith('2')).reduce((a, [, v]) => a + v, 0);
                      const s4xx = Object.entries(d.by_status || {}).filter(([k]) => k.startsWith('4')).reduce((a, [, v]) => a + v, 0);
                      const s5xx = Object.entries(d.by_status || {}).filter(([k]) => k.startsWith('5')).reduce((a, [, v]) => a + v, 0);
                      const pct = usage.daily_limit ? Math.round((d.total_calls / usage.daily_limit) * 100) : 0;
                      return (
                        <tr key={i} className="border-b border-[hsl(var(--border))]/30 hover:bg-[hsl(var(--card))]/40">
                          <td className="p-3 font-mono">{d.date}</td>
                          <td className="p-3 text-right font-mono text-cyan-400">{d.total_calls}</td>
                          <td className="p-3 text-right font-mono text-emerald-400">{s2xx}</td>
                          <td className="p-3 text-right font-mono text-amber-400">{s4xx}</td>
                          <td className="p-3 text-right font-mono text-red-400">{s5xx}</td>
                          <td className="p-3 text-right">
                            {usage.daily_limit ? (
                              <span className={`font-mono ${pct > 90 ? 'text-red-400' : pct > 70 ? 'text-amber-400' : 'text-[hsl(var(--muted-foreground))]'}`}>
                                {pct}%
                              </span>
                            ) : <span className="text-[hsl(var(--muted-foreground))]">-</span>}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No API usage data yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'earnings' && (
          <div data-testid="tab-earnings-content">
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Source</th>
                      <th className="text-right p-3">Gross</th>
                      <th className="text-right p-3">Commission</th>
                      <th className="text-right p-3">Net</th>
                      <th className="text-right p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.earnings?.length > 0 ? earnings.earnings.map((e, i) => (
                      <tr key={i} className="border-b border-[hsl(var(--border))]/30 hover:bg-[hsl(var(--card))]/40">
                        <td className="p-3 font-mono text-[hsl(var(--muted-foreground))]">{e.created_at ? new Date(e.created_at).toLocaleDateString() : '-'}</td>
                        <td className="p-3">{e.source_type || e.reference_id || '-'}</td>
                        <td className="p-3 text-right font-mono">${(e.gross_usdc || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-[hsl(var(--muted-foreground))]">${(e.commission_usdc || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-emerald-400">${(e.net_usdc || 0).toFixed(2)}</td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[e.status] || 'text-gray-400 bg-gray-500/10'}`}>
                            {e.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No earnings yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {earnings.total > 0 && (
                <Paginator page={earnings.page} hasMore={earnings.has_more} onPrev={() => loadEarnings(earnings.page - 1)} onNext={() => loadEarnings(earnings.page + 1)} />
              )}
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div data-testid="tab-tasks-content">
            <div className="flex gap-2 mb-4">
              {['', 'claimed', 'approved', 'completed', 'failed', 'abandoned'].map(s => (
                <button
                  key={s}
                  onClick={() => { setTaskFilter(s); loadTasks(1, s); }}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                    taskFilter === s ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[hsl(var(--secondary))]/30 text-[hsl(var(--muted-foreground))] border border-transparent hover:border-[hsl(var(--border))]'
                  }`}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                      <th className="text-left p-3">Title</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-right p-3">Bounty</th>
                      <th className="text-right p-3">Status</th>
                      <th className="text-right p-3">Date</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.tasks?.length > 0 ? tasks.tasks.map((t, i) => (
                      <tr key={i} className="border-b border-[hsl(var(--border))]/30 hover:bg-[hsl(var(--card))]/40">
                        <td className="p-3 max-w-[250px] truncate">
                          <Link to={`/bounties/${t.task_id}`} className="hover:text-emerald-400 transition-colors">
                            {t.title || t.task_id}
                          </Link>
                        </td>
                        <td className="p-3 font-mono text-[hsl(var(--muted-foreground))]">{t.task_type || '-'}</td>
                        <td className="p-3 text-right font-mono text-emerald-400">${(t.bounty_usdc || 0).toFixed(2)}</td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[t.status] || 'text-gray-400 bg-gray-500/10'}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-[hsl(var(--muted-foreground))]">{t.claimed_at ? new Date(t.claimed_at).toLocaleDateString() : '-'}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {t.status === 'claimed' && (
                              <>
                                <Link to={`/bounties/${t.task_id}`} data-testid={`task-submit-${t.task_id}`} className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors">
                                  Submit
                                </Link>
                                <button
                                  data-testid={`task-abandon-${t.task_id}`}
                                  onClick={async () => {
                                    if (!window.confirm('Abandon this task? Score penalty applies.')) return;
                                    try {
                                      await abandonTask(apiKey, t.task_id);
                                      loadTasks(tasks.page, taskFilter);
                                    } catch (err) { alert(err.message); }
                                  }}
                                  className="px-2 py-1 rounded-full text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors"
                                >
                                  Abandon
                                </button>
                              </>
                            )}
                            {(t.status === 'submitted' || t.status === 'under_review' || t.status === 'approved' || t.status === 'completed') && (
                              <Link to={`/bounties/${t.task_id}`} data-testid={`task-view-${t.task_id}`} className="px-2 py-1 rounded-full text-xs text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))] hover:text-[hsl(var(--foreground))] transition-colors inline-flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" /> View
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No tasks found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {tasks.total > 0 && (
                <Paginator page={tasks.page} hasMore={tasks.has_more} onPrev={() => loadTasks(tasks.page - 1, taskFilter)} onNext={() => loadTasks(tasks.page + 1, taskFilter)} />
              )}
            </div>
          </div>
        )}

        {activeTab === 'disputes' && (
          <div data-testid="tab-disputes-content">
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                      <th className="text-left p-3">Dispute ID</th>
                      <th className="text-left p-3">Reference</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-left p-3">Role</th>
                      <th className="text-right p-3">Status</th>
                      <th className="text-right p-3">Filed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disputes.disputes?.length > 0 ? disputes.disputes.map((d, i) => (
                      <tr key={i} className="border-b border-[hsl(var(--border))]/30 hover:bg-[hsl(var(--card))]/40">
                        <td className="p-3 font-mono text-xs">{d.dispute_id}</td>
                        <td className="p-3 font-mono text-xs text-[hsl(var(--muted-foreground))]">{d.reference_id}</td>
                        <td className="p-3 text-right font-mono text-emerald-400">${(d.amount_usdc || 0).toFixed(2)}</td>
                        <td className="p-3">
                          <span className={d.filed_by === overview?.agent_id ? 'text-blue-400' : 'text-amber-400'}>
                            {d.filed_by === overview?.agent_id ? 'Filed by me' : 'Filed against me'}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[d.status] || 'text-gray-400 bg-gray-500/10'}`}>
                            {d.status?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-[hsl(var(--muted-foreground))]">{d.filed_at ? new Date(d.filed_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400/30" />
                          No disputes — keep up the good work!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {disputes.total > 0 && (
                <Paginator page={disputes.page} hasMore={disputes.has_more} onPrev={() => loadDisputes(disputes.page - 1)} onNext={() => loadDisputes(disputes.page + 1)} />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
